import _, { get, size } from "lodash";
import { useEffect, useState } from "react";
import { trackPromise } from "react-promise-tracker";

import { Button, cn, constructUriWithSearchParams } from "@shared/ui";
import Loader from "../components/Loader";
import StepBody from "../components/StepBody";
import StepFooter from "../components/StepFooter";
import StepHeading from "../components/StepHeading";
import { updateIgnition } from "../lib/axle";

const Exit = ({
  step,
  session,
  client,
  loginInformation,
  history,
  previousStep,
  posthog,
  nextStep,
}) => {
  const [isRedirectEnabled, setRedirectEnabled] = useState(
    session.redirectUri || session.origin
  );

  // When confirmationBeforeRedirect is true, show the exit message and wait for user to click before redirecting
  const confirmationBeforeRedirect = get(
    session,
    "config.confirmationBeforeRedirect",
    false
  );

  // Track whether the async exit flow has completed (so the confirmation button can be shown)
  const [isProcessingComplete, setIsProcessingComplete] = useState(false);

  if (previousStep === "exit-confirmation") {
    if (size(history) > 1) {
      previousStep = history[size(history) - 2];
    } else {
      previousStep = "consent";
    }
  }

  if (previousStep === "exit-survey") {
    if (size(history) > 2) {
      previousStep = history[size(history) - 3];
    } else {
      previousStep = "consent";
    }
  }

  useEffect(() => {
    posthog.capture("$pageview", {
      step,
      carrier: get(loginInformation, "carrier.id"),
    });
    const exit = async () => {
      // Construct webhook event
      const lastEvent = {
        type: `ignition.exited`,
        data: {
          token: session.id,
          // config: session.config,
          step: previousStep,
          user: session.user,
          client: client.id,
        },
        createdAt: new Date().toISOString(),
      };

      // Update Ignition session
      const params = {
        status: "exited",
        lastEvent,
      };

      const [updateIgnitionResponse, updateIgnitionError] =
        await updateIgnition(session.id, params);

      if (updateIgnitionError) {
        switch (get(updateIgnitionError, "code")) {
          case 500:
            console.log(JSON.stringify(updateIgnitionError));
            nextStep("axle-error");
            return;
          default:
            nextStep("connection-error");
            return;
        }
      }

      if (isRedirectEnabled) {
        if (confirmationBeforeRedirect) {
          // Wait for user confirmation before redirecting
          setIsProcessingComplete(true);
        } else {
          await sendEventAndRedirect();
        }
      }
    };
    trackPromise(exit());
  }, []);

  const sendEventAndRedirect = async () => {
    // Create params for postMessage or redirectUri
    const paramsToAdd = {
      status: "exit",
      step: previousStep,
      client: client.id,
    };

    // Send message to parent window if initialized in an iframe
    if (session.origin) {
      const sessionOrigin = session.origin;
      window.parent.postMessage(paramsToAdd, sessionOrigin);
      console.log(`Sending message to session origin ${session.origin}`);
      console.log(
        "Message sent to parent window",
        JSON.stringify(paramsToAdd, null, 2)
      );
    }

    // If redirectUri, change window location to redirect
    if (session.redirectUri) {
      const constructedUri = constructUriWithSearchParams(
        session.redirectUri,
        paramsToAdd
      );
      window.location.href = constructedUri;
    }
  };

  // Handler for the confirmation button click
  const handleConfirmRedirect = async () => {
    await sendEventAndRedirect();
  };

  // Determine whether to show the message content vs a loader
  const showMessageContent =
    !isRedirectEnabled || (confirmationBeforeRedirect && isProcessingComplete);
  const hasFooter = isRedirectEnabled && confirmationBeforeRedirect;

  return (
    <>
      {showMessageContent ? (
        <>
          <StepBody
            centered
            className={cn(
              "items-center gap-4 text-center",
              // Reserve a footer's height so the content sits as it would with one, like Success.
              !hasFooter && "pb-24"
            )}
          >
            <div
              style={{ backgroundImage: `url("./icons/exit.svg")` }}
              className="h-32 w-full flex-none bg-contain bg-center bg-no-repeat"
              aria-hidden
            ></div>
            <StepHeading
              align="center"
              title={_(session).get("config.exit.header") ?? "Thanks!"}
              description={
                _(session).get("config.exit.subheader") ??
                `${client.displayName} will reach out to complete verification.`
              }
            />
          </StepBody>
          {hasFooter && (
            <StepFooter>
              <Button
                onClick={handleConfirmRedirect}
                name="exit-confirm-redirect"
              >
                Continue
              </Button>
            </StepFooter>
          )}
        </>
      ) : (
        <StepBody>
          <Loader isOpen={true} />
        </StepBody>
      )}
    </>
  );
};

export default Exit;
